import {
    Avatar,
    Badge,
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Empty,
    List,
    Row,
    Space,
    Table,
    Tag,
    Typography
} from 'antd';
import {
    CalendarOutlined,
    CarOutlined,
    CrownOutlined,
    GiftOutlined,
    SearchOutlined,
    TeamOutlined,
    UserOutlined,
    AppleOutlined,
    AndroidOutlined,
    DownloadOutlined,
    MobileOutlined
} from '@ant-design/icons';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { apiCall } from '../../API/RestApi';
import { UPDATE_MENU } from '../../Redux/actions/userActions';
import { dateFormat } from '../../Utils/formatDate';
import { modalError } from '../../Component/Modal/ModalLayout';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

function Dashboard() {
    const token = useSelector((state) => state.user.token);
    const user = useSelector((state) => state.user.user);
    const currentUser = JSON.parse(user);
    const isDivision30 = Number(currentUser.division_id) === 30;

    const dispatch = useDispatch();

    const today = dayjs();
    const [dateRange, setDateRange] = useState([today.startOf('month'), today]);

    const [loading, setLoading] = useState(false);
    const [totalUser, setTotalUser] = useState(0);

    const [exemplaryEmployees, setExemplaryEmployees] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [loans, setLoans] = useState([]);
    const [birthdays, setBirthdays] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [userLog, setUserLog] = useState([]);

    useEffect(() => {
        fetchDashboard();
    }, []);

    const authHeaders = {
        Authorization: 'Bearer ' + token
    };

    const fetchDashboard = () => {
        const [start, end] = dateRange;

        const startDate = start
            ? dayjs(start).format('YYYY-MM-DD')
            : today.startOf('month').format('YYYY-MM-DD');

        const endDate = end
            ? dayjs(end).format('YYYY-MM-DD')
            : today.format('YYYY-MM-DD');

        // TOTAL USER
        apiCall({
            method: 'GET',
            url: `/dashboard?start_date=${startDate}&end_date=${endDate}`,
            data: { headers: authHeaders }
        }).then((res) => {
            if (res?.status === 'success') {
                setTotalUser(res?.data?.total_user || 0);
            }
        });

        // MEETING
        apiCall({
            method: 'GET',
            url: `/analytic/meeting`,
            data: { headers: authHeaders }
        }).then((res) => {
            if (res?.status === 'success') {
                setMeetings(res?.data || []);
            }
        });

        // LOAN
        apiCall({
            method: 'GET',
            url: `/analytic/loan`,
            data: { headers: authHeaders }
        }).then((res) => {
            if (res?.status === 'success') {
                setLoans(res?.data || []);
            }
        });

        // BIRTHDAY
        apiCall({
            method: 'GET',
            url: `/analytic/birthday`,
            data: { headers: authHeaders }
        }).then((res) => {
            if (res?.status === 'success') {
                setBirthdays(res?.data || []);
            }
        });

        // USER LEAVE
        apiCall({
            method: 'GET',
            url: `/userleave?date_start=${startDate}&date_end=${endDate}`,
            data: { headers: authHeaders }
        }).then((res) => {
            if (res?.status === 'success') {
                setLeaves(res?.data || []);
            }
        });

        // USER LOG
        apiCall({
            method: 'GET',
            url: `/userlog?page=1&startDate=${startDate}&endDate=${endDate}`,
            data: { headers: authHeaders }
        }).then((res) => {
            if (res?.status === 'success') {
                setUserLog(res?.data?.data || []);
            }
        });

        // EXEMPLARY EMPLOYEE (PALING LAMA)
        apiCall({
            method: 'GET',
            url: `/analytic/exemplary-employee?start_date=${startDate}&end_date=${endDate}`,
            data: { headers: authHeaders }
        }).then((res) => {
            if (res?.status === 'success') {
                setExemplaryEmployees(res?.data || []);
            }
        });

        // MENU
        apiCall({
            method: 'GET',
            url: `/getusermenu`,
            data: { headers: authHeaders }
        }).then((res) => {
            if (res?.status === 'success') {
                dispatch(UPDATE_MENU(JSON.stringify(res.data)));
            }
        });
    };

    const appDownloads = [
        {
            platform: 'iOS',
            subtitle: 'Download BOLDe Access ID dari App Store',
            icon: <AppleOutlined />,
            actionIcon: <DownloadOutlined />,
            url: 'https://apps.apple.com/us/app/bolde-access-id/id6737598638',
            className: 'is-ios'
        },
        {
            platform: 'Android',
            subtitle: 'Download aplikasi Android melalui Google Drive',
            icon: <AndroidOutlined />,
            actionIcon: <DownloadOutlined />,
            url: 'https://drive.google.com/drive/folders/1tr2pVi0mdJO0eHmSf1sKl6_2Oaz-79fm?usp=sharing',
            className: 'is-android'
        }
    ];

    const summaryCards = [
        {
            title: 'Total Users',
            value: totalUser,
            icon: <TeamOutlined />,
            color: '#1677ff',
            bg: 'linear-gradient(135deg, #1677ff, #69b1ff)'
        },
        {
            title: 'Exemplary Employees',
            value: exemplaryEmployees.length,
            icon: <CrownOutlined />,
            color: '#faad14',
            bg: 'linear-gradient(135deg, #fa8c16, #ffd666)'
        },
        {
            title: 'Meeting Today',
            value: meetings.length,
            icon: <CalendarOutlined />,
            color: '#722ed1',
            bg: 'linear-gradient(135deg, #531dab, #b37feb)'
        },
        {
            title: 'Loan Today',
            value: loans.length,
            icon: <CarOutlined />,
            color: '#13c2c2',
            bg: 'linear-gradient(135deg, #08979c, #5cdbd3)'
        },
        {
            title: 'Birthday Today',
            value: birthdays.length,
            icon: <GiftOutlined />,
            color: '#eb2f96',
            bg: 'linear-gradient(135deg, #c41d7f, #ff85c0)'
        },
        {
            title: 'On Leave',
            value: leaves.length,
            icon: <UserOutlined />,
            color: '#52c41a',
            bg: 'linear-gradient(135deg, #389e0d, #95de64)'
        }
    ];

    const exemplaryColumns = [
        { title: 'No', render: (_, __, index) => index + 1, width: 60 },
        {
            title: 'Employee',
            render: (_, record) => (
                <Space>
                    <Avatar src={record.image} icon={<UserOutlined />} />
                    <div>
                        <Text strong>{record.name}</Text>
                        <br />
                        <Text type="secondary">{record.user_code}</Text>
                    </div>
                </Space>
            )
        },
        { title: 'Division', dataIndex: 'division' },
        { title: 'Position', dataIndex: 'position' },
        {
            title: 'Status',
            render: () => <Tag color="gold">Teladan</Tag>
        }
    ];

    const meetingColumns = [
        { title: 'No', render: (_, __, index) => index + 1, width: 60 },
        { title: 'Time', render: (_, r) => `${r.time_start || '-'} - ${r.time_end || '-'}` },
        { title: 'Penyelenggara', render: (_, r) => r.users?.name || '-' },
        { title: 'Description', dataIndex: 'description' },
        { title: 'Location', render: (_, r) => r.meeting_location?.name || r.location || '-' }
    ];

    const loanColumns = [
        { title: 'No', render: (_, __, index) => index + 1, width: 60 },
        { title: 'User', render: (_, r) => r.user_request?.name || '-' },
        { title: 'Vehicle', render: (_, r) => r.vehicle?.brand + ' ' + r.vehicle?.license_plate || '-' },
        { title: 'Purpose', dataIndex: 'purpose' },
        {
            title: 'Status',
            dataIndex: 'approval_status',
            render: (status) => {
                const color = status === 'Approved' ? 'green' : status === 'Rejected' ? 'red' : 'orange';
                return <Tag color={color}>{status}</Tag>;
            }
        }
    ];

    const leaveColumns = [
        { title: 'No', render: (_, __, index) => index + 1, width: 60 },
        {
            title: 'Employee',
            render: (_, record) => (
                <Space>
                    <Avatar src={record.image} icon={<UserOutlined />} />
                    <div>
                        <Text strong>{record.name}</Text>
                        <br />
                        <Text type="secondary">{record.users_code}</Text>
                    </div>
                </Space>
            )
        },
        { title: 'Division', render: (_, r) => r.division?.name || '-' },
        { title: 'Position', render: (_, r) => r.position?.name || '-' },
        {
            title: 'Leave',
            render: (_, r) => (
                <Tag color="blue">
                    {r.leave_paid?.length || 0} Request
                </Tag>
            )
        }
    ];

    const userLogColumns = [
        { title: 'No', render: (_, __, index) => index + 1, width: 60 },
        { title: 'Users Code', dataIndex: 'users_code' },
        { title: 'Name', dataIndex: 'users_name' },
        { title: 'Desc', dataIndex: 'desc' },
        {
            title: 'Date',
            dataIndex: 'created_at',
            render: (text) => dateFormat(text)
        }
    ];

    return (
        <>
            <Helmet>
                <title>Dashboard</title>
            </Helmet>

            <div
                style={{
                    padding: 24,
                    borderRadius: 24,
                    marginBottom: 24,
                    background: 'linear-gradient(135deg, #0f172a, #1e293b)',
                    color: '#fff'
                }}
            >
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col>
                        <Title level={2} style={{ color: '#fff', marginBottom: 4 }}>
                            Dashboard
                        </Title>
                        <Text style={{ color: '#cbd5e1' }}>
                            Ringkasan aktivitas karyawan hari ini dan periode pilihan.
                        </Text>
                    </Col>
                    <Col>
                        <Space>
                            <RangePicker
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates || [today.startOf('month'), today])}
                            />
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={fetchDashboard}
                                loading={loading}
                            >
                                Filter
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            <Row gutter={[16, 16]}>
                {summaryCards.map((item) => (
                    <Col key={item.title} xs={24} sm={12} md={8} lg={8} xl={4}>
                        <Card
                            loading={loading}
                            bordered={false}
                            style={{
                                borderRadius: 22,
                                background: item.bg,
                                color: '#fff',
                                boxShadow: '0 14px 28px rgba(15, 23, 42, 0.16)'
                            }}
                        >
                            <Space direction="vertical" size={8}>
                                <div style={{ fontSize: 28 }}>{item.icon}</div>
                                <Text style={{ color: '#fff', opacity: 0.88 }}>{item.title}</Text>
                                <div style={{ fontSize: 32, fontWeight: 800 }}>{item.value}</div>
                            </Space>
                        </Card>
                    </Col>
                ))}
            </Row>

            <section className="dashboard-app-download" aria-label="Download aplikasi BOLDe Access">
                <div className="dashboard-app-download__intro">
                    <div className="dashboard-app-download__icon"><MobileOutlined /></div>
                    <div>
                        <Text className="dashboard-app-download__eyebrow">MOBILE APP</Text>
                        <Title level={3} className="dashboard-app-download__title">BOLDe Access di smartphone Anda</Title>
                        <Text className="dashboard-app-download__description">
                            Akses fitur HR lebih praktis dari iPhone maupun Android. Pilih platform untuk membuka halaman download resmi.
                        </Text>
                    </div>
                </div>
                <div className="dashboard-app-download__actions">
                    {appDownloads.map((app) => (
                        <a
                            key={app.platform}
                            className={`dashboard-store-card ${app.className}`}
                            href={app.url}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            <span className="dashboard-store-card__icon">{app.icon}</span>
                            <span className="dashboard-store-card__copy">
                                <span className="dashboard-store-card__label">Download untuk</span>
                                <strong>{app.platform}</strong>
                                <small>{app.subtitle}</small>
                            </span>
                            <span className="dashboard-store-card__action">{app.actionIcon}</span>
                        </a>
                    ))}
                </div>
            </section>

            <Divider />

            <Row gutter={[16, 16]}>
                {isDivision30 && (
                    <Col xs={24} xl={12}>
                        <Card
                            title={
                                <Space>
                                    <CrownOutlined />
                                    Exemplary Employees
                                </Space>
                            }
                            bordered={false}
                            style={{ borderRadius: 20 }}
                        >
                            <Table
                                size="middle"
                                columns={exemplaryColumns}
                                dataSource={exemplaryEmployees}
                                loading={loading}
                                pagination={{ pageSize: 5 }}
                                rowKey="user_id"
                            />
                        </Card>
                    </Col>
                )}

                <Col xs={24} xl={12}>
                    <Card
                        title={
                            <Space>
                                <CalendarOutlined />
                                Meeting Today
                            </Space>
                        }
                        bordered={false}
                        style={{ borderRadius: 20 }}
                    >
                        <Table
                            size="middle"
                            columns={meetingColumns}
                            dataSource={meetings}
                            loading={loading}
                            pagination={{ pageSize: 5 }}
                            rowKey="meeting_schedule_id"
                        />
                    </Card>
                </Col>

                <Col xs={24} xl={12}>
                    <Card
                        title={
                            <Space>
                                <CarOutlined />
                                Loan Today
                            </Space>
                        }
                        bordered={false}
                        style={{ borderRadius: 20 }}
                    >
                        <Table
                            size="middle"
                            columns={loanColumns}
                            dataSource={loans}
                            loading={loading}
                            pagination={{ pageSize: 5 }}
                            rowKey="loan_request_id"
                        />
                    </Card>
                </Col>

                <Col xs={24} xl={12}>
                    <Card
                        title={
                            <Space>
                                <GiftOutlined />
                                Birthday Today
                            </Space>
                        }
                        bordered={false}
                        style={{ borderRadius: 20 }}
                    >
                        {birthdays.length === 0 ? (
                            <Empty description="Tidak ada ulang tahun hari ini" />
                        ) : (
                            <List
                                loading={loading}
                                itemLayout="horizontal"
                                dataSource={birthdays}
                                renderItem={(item) => (
                                    <List.Item>
                                        <List.Item.Meta
                                            avatar={<Avatar src={item.image} icon={<UserOutlined />} />}
                                            title={<Text strong>{item.name}</Text>}
                                            description={
                                                <Space direction="vertical" size={0}>
                                                    <Text type="secondary">
                                                        {item.position?.name || '-'} · {item.division?.name || '-'}
                                                    </Text>
                                                    <Badge color="pink" text="Birthday Today" />
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>
                </Col>

                <Col xs={24}>
                    <Card
                        title={
                            <Space>
                                <UserOutlined />
                                Employees On Leave
                            </Space>
                        }
                        bordered={false}
                        style={{ borderRadius: 20 }}
                    >
                        <Table
                            size="middle"
                            columns={leaveColumns}
                            dataSource={leaves}
                            loading={loading}
                            pagination={{ pageSize: 5 }}
                            rowKey="users_id"
                        />
                    </Card>
                </Col>

                <Col xs={24}>
                    <Card
                        title="Users Activities"
                        bordered={false}
                        style={{ borderRadius: 20 }}
                    >
                        <Table
                            size="middle"
                            columns={userLogColumns}
                            dataSource={userLog}
                            loading={loading}
                            pagination={false}
                            rowKey={(record, index) => record.user_log_id || index}
                        />
                    </Card>
                </Col>
            </Row>
        </>
    );
}

export default Dashboard;